import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Building2, Search, UserRound } from 'lucide-react';
import { api } from '@/shared/api/cliente';
import { ErrorDeApi } from '@/shared/api/problema';
import { useValorDiferido } from '@/shared/ui/useValorDiferido';
import type { Titulo } from './contrato';

export interface Deudor {
  identificacion?: string;
  nombre?: string;
  saldo: number;
  titulos: Titulo[];
}

export function SeleccionDePersona({
  pregunta,
  busqueda,
  alBuscar,
  deudor,
  alElegir,
  alLimpiar,
  textoInicial = '',
}: {
  pregunta: string;
  busqueda: string;
  alBuscar: (v: string) => void;
  deudor: Deudor | null;
  alElegir: (d: Deudor) => void;
  alLimpiar: () => void;
  textoInicial?: string;
}) {
  const buscado = useValorDiferido(busqueda.trim());
  const buscable = /^\d+$/.test(buscado) || buscado.length >= 3;

  const deudores = useQuery({
    queryKey: ['titulos-deudores', buscado],
    queryFn: () =>
      api.get<{ deudores: Deudor[]; hayMas: boolean }>('/titulos/deudores', {
        query: { texto: buscado },
      }),
    enabled: deudor === null && buscable,
  });

  const autoElegido = useRef(textoInicial === '');
  const unica = deudores.data?.deudores.length === 1 ? deudores.data.deudores[0] : undefined;
  useEffect(() => {
    if (!autoElegido.current && unica) {
      autoElegido.current = true;
      alElegir(unica);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unica]);

  return (
    <div className="mb-5 rounded-2xl border border-armazon-200 bg-white p-5">
      {deudor ? (
        <div className="flex items-center justify-between gap-3">
          <span className="flex min-w-0 items-center gap-3">
            <Icono identificacion={deudor.identificacion} grande />
            <span className="min-w-0">
              <span className="block truncate text-sm font-bold uppercase text-armazon-800">
                {deudor.nombre ?? 'Sin nombre en el origen'}
              </span>
              {deudor.identificacion ? (
                <span className="importe block text-xs text-armazon-500">
                  {deudor.identificacion}
                </span>
              ) : (
                <SinCedula />
              )}
            </span>
          </span>
          <button
            type="button"
            onClick={alLimpiar}
            className="shrink-0 text-[11px] font-semibold uppercase text-marca-700 hover:underline"
          >
            Atender a otra persona
          </button>
        </div>
      ) : (
        <label className="grid gap-2">
          <span className="text-sm font-semibold uppercase text-armazon-900">{pregunta}</span>
          <span className="relative">
            <Search
              className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-armazon-400"
              aria-hidden
            />
            <input
              value={busqueda}
              onChange={(e) => alBuscar(e.target.value)}
              placeholder="Cédula, código anterior del título o nombre…"
              autoFocus
              className="h-11 w-full rounded-lg border border-armazon-200 bg-white pl-9 pr-3 text-sm outline-none transition-colors focus:border-marca-400"
            />
          </span>

          {buscable && (
            <ul className="max-h-72 overflow-y-auto rounded-lg border border-armazon-200">
              {deudores.isLoading && (
                <li className="px-3 py-2 text-xs uppercase text-armazon-400">Buscando…</li>
              )}
              {deudores.isError && (
                <li className="px-3 py-2 text-xs uppercase text-red-700">
                  {deudores.error instanceof ErrorDeApi
                    ? deudores.error.message
                    : 'No se pudo buscar'}
                </li>
              )}
              {deudores.data && deudores.data.deudores.length === 0 && (
                <li className="px-3 py-2 text-xs uppercase text-armazon-400">
                  Nadie con títulos pendientes coincide
                </li>
              )}
              {deudores.data?.deudores.map((d) => (
                <li key={`${d.identificacion ?? ''}|${d.nombre ?? ''}|${d.titulos[0]?.id}`}>
                  <button
                    type="button"
                    onClick={() => alElegir(d)}
                    className="flex w-full items-center gap-2.5 border-b border-armazon-100 px-3 py-2 text-left transition-colors last:border-b-0 hover:bg-armazon-50"
                  >
                    <Icono identificacion={d.identificacion} />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-xs font-medium uppercase text-armazon-800">
                        {d.nombre ?? 'Sin nombre en el origen'}
                      </span>
                      {d.identificacion ? (
                        <span className="importe block text-[10px] text-armazon-500">
                          {d.identificacion}
                        </span>
                      ) : (
                        <SinCedula />
                      )}
                    </span>
                    <span className="shrink-0 text-right">
                      <span className="importe block text-xs font-bold text-armazon-800">
                        ${d.saldo.toFixed(2)}
                      </span>
                      <span className="block text-[10px] uppercase text-armazon-400">
                        {d.titulos.length} {d.titulos.length === 1 ? 'título' : 'títulos'}
                      </span>
                    </span>
                  </button>
                </li>
              ))}
              {deudores.data?.hayMas && (
                <li className="bg-armazon-50 px-3 py-2 text-[10px] uppercase text-armazon-500">
                  Hay más coincidencias: escriba el nombre completo o la cédula
                </li>
              )}
            </ul>
          )}
        </label>
      )}
    </div>
  );
}

export function ListaDeTitulos({
  titulos,
  desmarcados,
  alCambiar,
}: {
  titulos: Titulo[];
  desmarcados: Set<number>;
  alCambiar: (desmarcados: Set<number>) => void;
}) {
  function alternar(id: number) {
    const siguiente = new Set(desmarcados);
    if (siguiente.has(id)) siguiente.delete(id);
    else siguiente.add(id);
    alCambiar(siguiente);
  }

  return (
    <div className="overflow-hidden rounded-2xl border border-armazon-200 bg-white shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-armazon-100 px-5 py-3">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-armazon-600">
          {titulos.length} {titulos.length === 1 ? 'título pendiente' : 'títulos pendientes'} · de
          la más vieja a la más nueva
        </p>
        <span className="flex gap-1">
          <Atajo alPulsar={() => alCambiar(new Set())} activo={desmarcados.size === 0}>
            Todos
          </Atajo>
          <Atajo
            alPulsar={() => alCambiar(new Set(titulos.map((t) => t.id)))}
            activo={desmarcados.size === titulos.length}
          >
            Ninguno
          </Atajo>
        </span>
      </div>

      <div className="max-h-[22rem] divide-y divide-armazon-100 overflow-y-auto">
        {titulos.map((t) => (
          <FilaDeTitulo
            key={t.id}
            titulo={t}
            marcado={!desmarcados.has(t.id)}
            alAlternar={() => alternar(t.id)}
          />
        ))}
      </div>
    </div>
  );
}

function FilaDeTitulo({
  titulo: t,
  marcado,
  alAlternar,
}: {
  titulo: Titulo;
  marcado: boolean;
  alAlternar: () => void;
}) {
  return (
    <label
      className={`flex cursor-pointer flex-wrap items-center gap-3 px-5 py-3 transition-colors ${
        marcado ? 'bg-marca-50/40' : 'hover:bg-armazon-50/60'
      }`}
    >
      <input
        type="checkbox"
        checked={marcado}
        onChange={alAlternar}
        className="size-4 shrink-0 accent-marca-600"
      />
      <span className="importe w-24 shrink-0 text-xs font-semibold text-armazon-800">
        {t.codigo ?? t.id}
      </span>
      <span className="min-w-0 flex-1 basis-52">
        <span className="importe block text-[11px] text-armazon-700">
          {enMes(t.desde)}
          {t.hasta && t.hasta !== t.desde ? ` – ${enMes(t.hasta)}` : ''}
        </span>
        <span className="block truncate text-[10px] uppercase text-armazon-400">
          {t.meses != null ? `${t.meses} ${t.meses === 1 ? 'mes' : 'meses'}` : 'Sin meses'}
          {t.anexos ? ` · ${t.anexos}` : ''}
        </span>
      </span>
      <span className="importe w-24 shrink-0 text-right text-sm font-semibold text-armazon-800">
        ${t.capital.toFixed(2)}
      </span>
    </label>
  );
}

export function SinCedula() {
  return (
    <span className="mt-0.5 inline-block rounded bg-amber-50 px-1 text-[9px] font-bold uppercase text-amber-700">
      Sin cédula en el SIRE
    </span>
  );
}

function Icono({
  identificacion,
  grande = false,
}: {
  identificacion?: string | undefined;
  grande?: boolean;
}) {
  const esInstitucion = (identificacion ?? '').length === 13;
  const caja = grande ? 'size-10' : 'size-7';
  const trazo = grande ? 'size-5' : 'size-3.5';
  return (
    <span
      className={`grid ${caja} shrink-0 place-items-center rounded-lg ${
        !identificacion || esInstitucion
          ? 'bg-amber-50 text-amber-600'
          : 'bg-indigo-50 text-indigo-500'
      }`}
    >
      {esInstitucion ? (
        <Building2 className={trazo} aria-hidden />
      ) : (
        <UserRound className={trazo} aria-hidden />
      )}
    </span>
  );
}

function Atajo({
  alPulsar,
  activo,
  children,
}: {
  alPulsar: () => void;
  activo: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={alPulsar}
      className={`rounded-md px-2.5 py-1 text-[10px] font-semibold uppercase transition-colors ${
        activo ? 'bg-armazon-800 text-white' : 'text-armazon-500 hover:bg-armazon-100'
      }`}
    >
      {children}
    </button>
  );
}

export function Campo({ etiqueta, children }: { etiqueta: string; children: React.ReactNode }) {
  return (
    <label className="grid gap-1.5">
      <span className="text-[10px] font-semibold uppercase text-armazon-600">{etiqueta}</span>
      {children}
    </label>
  );
}

const MESES = ['ENE', 'FEB', 'MAR', 'ABR', 'MAY', 'JUN', 'JUL', 'AGO', 'SEP', 'OCT', 'NOV', 'DIC'];

export function enMes(iso: string | undefined): string {
  if (!iso) return 'Sin período';
  const [anio, mes] = iso.split('-');
  return `${MESES[Number(mes) - 1]} ${anio}`;
}

export const ENTRADA =
  'h-10 w-full rounded-lg border border-armazon-200 bg-white px-3 text-sm text-armazon-900 outline-none transition-colors focus:border-marca-400';
